import React, { useState, useEffect } from "react";

import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  ListItemText,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { ScreenShareOutlined } from "@mui/icons-material";

import ModernModal from "../../../common/ModernModal";
import { editorModalTheme } from "../../theme/editorModalTheme";
import {
  ExportVideoCompressionLevel,
  ExportVideoResolution,
} from "../../types";
import { AspectRatio } from "../AspectRatioSelector";

export interface ExportVideoModalProps {
  open: boolean;
  handleClose: () => void;
  handleExport: (
    video_resolution: ExportVideoResolution,
    compression_level: ExportVideoCompressionLevel,
    fps: number,
    includeSubtitle: boolean,
    font?: string,
  ) => void;
  currentAspectRatio?: AspectRatio;
  currentFont?: string;
}

export default function ExportVideoModal({
  open,
  handleClose,
  handleExport,
  currentAspectRatio,
  currentFont,
}: ExportVideoModalProps) {
  const [compressionLevel, setCompressionLevel] = useState(
    ExportVideoCompressionLevel.BALANCED,
  );
  const [resolution, setResolution] = useState(ExportVideoResolution["1080p"]);
  const [fps, setFps] = useState<number>(30);
  const [includeSubtitle, setIncludeSubtitle] = useState<boolean>(true);

  // Auto-select appropriate resolution based on current aspect ratio
  useEffect(() => {
    if (currentAspectRatio) {
      const aspectRatioId = currentAspectRatio.id;

      // Map aspect ratios to appropriate export resolutions
      const resolutionMap: Record<string, ExportVideoResolution> = {
        standard: ExportVideoResolution["1080p"], // Standard format defaults to 1080p
        "youtube-shorts": ExportVideoResolution["youtube-shorts"],
        instagram: ExportVideoResolution.instagram, // Instagram format uses Instagram-specific export
        tiktok: ExportVideoResolution.tiktok,
      };

      const recommendedResolution = resolutionMap[aspectRatioId];
      if (recommendedResolution) {
        setResolution(recommendedResolution);
      }
    }
  }, [currentAspectRatio]);

  const handleCompressionChange = (event: any) => {
    setCompressionLevel(event.target.value);
  };

  const handleResolutionChange = (event: any) => {
    setResolution(event.target.value);
  };

  const handleFpsChange = (event: any) => {
    setFps(event.target.value);
  };

  const handleExportClick = () => {
    handleExport(
      resolution,
      compressionLevel,
      fps,
      includeSubtitle,
      currentFont,
    );
    handleClose();
  };

  // Helper function to get format-specific warning message
  const getFormatWarningMessage = () => {
    if (currentAspectRatio?.id === "standard") {
      return " For Standard format, you can choose any quality level (320p, 480p, 720p, 1080p, or 4K).";
    }
    if (currentAspectRatio?.id === "instagram") {
      return " For Instagram format, you can choose any quality level (320p, 480p, 720p, 1080p, or 4K).";
    }
    return " Consider selecting a matching export format for best results.";
  };

  // Check if there's a mismatch between preview and export format
  const hasFormatMismatch = () => {
    if (!currentAspectRatio) return false;

    const aspectRatioId = currentAspectRatio.id;

    // For standard format, allow any of the standard quality options
    if (aspectRatioId === "standard") {
      const standardResolutions = [
        ExportVideoResolution["320p"],
        ExportVideoResolution["480p"],
        ExportVideoResolution["720p"],
        ExportVideoResolution["1080p"],
        ExportVideoResolution["4k"],
      ];
      return !standardResolutions.includes(resolution);
    }

    // For platform-specific formats, check exact match
    const platformMappings = {
      "youtube-shorts": ExportVideoResolution["youtube-shorts"],
      instagram: ExportVideoResolution.instagram,
      tiktok: ExportVideoResolution.tiktok,
    };

    return (
      platformMappings[aspectRatioId as keyof typeof platformMappings] !==
      resolution
    );
  };

  return (
    <ThemeProvider theme={editorModalTheme}>
      <ModernModal
        open={open}
        onClose={handleClose}
        title="Export Video"
        maxWidth="sm"
        actions={
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleExportClick}
              startIcon={<ScreenShareOutlined />}
              sx={{
                background: "linear-gradient(135deg, #10b981, #059669)",
                boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
                fontWeight: 600,
                "&:hover": {
                  background: "linear-gradient(135deg, #059669, #047857)",
                  boxShadow: "0 6px 20px rgba(16, 185, 129, 0.4)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              Export Video
            </Button>
          </Box>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Format Mismatch Warning */}
          {hasFormatMismatch() && (
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                bgcolor: "rgba(250, 204, 21, 0.1)",
                color: "text.primary",
                border: "1px solid rgba(250, 204, 21, 0.35)",
                "& .MuiAlert-icon": { color: "#fbbf24" },
              }}
            >
              <Typography variant="body2">
                <strong>Format Mismatch:</strong> Your preview is in{" "}
                {currentAspectRatio?.name} format, but you're exporting in a
                different format. This may result in cropping or letterboxing.
                {getFormatWarningMessage()}
              </Typography>
            </Alert>
          )}

          {/* Compression Level */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}
            >
              Quality & Speed
            </Typography>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Compression Level</InputLabel>
              <Select
                value={compressionLevel}
                onChange={handleCompressionChange}
                label="Compression Level"
              >
                <MenuItem value={ExportVideoCompressionLevel.HIGHER_QUALITY}>
                  <ListItemText
                    primary="Higher Quality"
                    secondary="Best visual quality, longer render times"
                  />
                </MenuItem>
                <MenuItem value={ExportVideoCompressionLevel.BALANCED}>
                  <ListItemText
                    primary="Balanced"
                    secondary="Good balance between quality and speed"
                  />
                </MenuItem>
                <MenuItem value={ExportVideoCompressionLevel.VERY_FAST}>
                  <ListItemText
                    primary="Very Fast"
                    secondary="Fast render times, lower quality"
                  />
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Resolution */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}
            >
              Resolution & Platform
            </Typography>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Resolution</InputLabel>
              <Select
                value={resolution}
                onChange={handleResolutionChange}
                label="Resolution"
              >
                <MenuItem value={ExportVideoResolution["320p"]}>
                  <ListItemText
                    primary="320p"
                    secondary="Low resolution, faster upload and processing"
                  />
                </MenuItem>
                <MenuItem value={ExportVideoResolution["480p"]}>
                  <ListItemText
                    primary="480p"
                    secondary="Standard definition, faster processing"
                  />
                </MenuItem>
                <MenuItem value={ExportVideoResolution["720p"]}>
                  <ListItemText
                    primary="720p"
                    secondary="High definition, standard for online videos"
                  />
                </MenuItem>
                <MenuItem value={ExportVideoResolution["1080p"]}>
                  <ListItemText
                    primary="1080p"
                    secondary="Full HD, good quality for most purposes"
                  />
                </MenuItem>
                <MenuItem value={ExportVideoResolution["4k"]}>
                  <ListItemText
                    primary="4K"
                    secondary="Ultra HD, highest quality with long render times"
                  />
                </MenuItem>
                <Divider sx={{ my: 1 }} />
                <MenuItem value={ExportVideoResolution.instagram}>
                  <ListItemText
                    primary="Instagram"
                    secondary="Optimized for Instagram stories"
                  />
                </MenuItem>
                <MenuItem value={ExportVideoResolution.tiktok}>
                  <ListItemText
                    primary="TikTok"
                    secondary="Optimized for TikTok format"
                  />
                </MenuItem>
                <MenuItem value={ExportVideoResolution["youtube-shorts"]}>
                  <ListItemText
                    primary="YouTube Shorts"
                    secondary="Optimized for YouTube Shorts"
                  />
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* FPS */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}
            >
              Frame Rate
            </Typography>
            <FormControl fullWidth variant="outlined">
              <InputLabel>FPS</InputLabel>
              <Select value={fps} onChange={handleFpsChange} label="FPS">
                <MenuItem value={24}>
                  <ListItemText
                    primary="24 FPS"
                    secondary="Cinematic feel, ideal for storytelling content"
                  />
                </MenuItem>
                <MenuItem value={30}>
                  <ListItemText
                    primary="30 FPS"
                    secondary="Standard for most online videos"
                  />
                </MenuItem>
                <MenuItem value={60}>
                  <ListItemText
                    primary="60 FPS"
                    secondary="Smooth motion, great for dynamic content"
                  />
                </MenuItem>
                <MenuItem value={120}>
                  <ListItemText
                    primary="120 FPS"
                    secondary="Ultra smooth, excellent for slow-motion scenes"
                  />
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Subtitles Toggle */}
          <Box
            sx={{
              p: 2,
              bgcolor: "rgba(255,255,255,0.04)",
              borderRadius: 2,
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={includeSubtitle}
                  onChange={(e) => setIncludeSubtitle(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Include Subtitles
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Add captions to your exported video for better accessibility
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Box>
      </ModernModal>
    </ThemeProvider>
  );
}
